<template>
  <div class="suitSelectComponent">
    <b-card no-body>
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div>Suit Selection</div>
        <div><b-button variant="outline-dark" size="sm" @click="reset">reset</b-button></div>
      </b-card-header>

      <b-card-body v-if="isAvailable">
        <div class="quickComboSelect">
          <b-list-group>
            <b-list-group-item v-for="suit in quickSelectableSuits" :key="suit">
              <div class="suit d-flex">
                <div class="checkboxContainer mr-4">
                  <b-form-checkbox
                    v-model="checkboxes[suit].any"
                    @change="value => toggleCheckbox(value, suit, 'any')"
                  >
                    <div class="d-flex align-items-start justify-content-end">
                      any <Suit class="ml-1" :suit="suit" />
                    </div>
                  </b-form-checkbox>
                </div>
                <div class="checkboxContainer">
                  <b-form-checkbox
                    v-model="checkboxes[suit].high"
                    @change="value => toggleCheckbox(value, suit, 'high')"
                  >
                    <div class="d-flex align-items-start justify-content-end">
                      high <Suit class="ml-1" :suit="suit" />
                    </div>
                  </b-form-checkbox>
                </div>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>

        <div class="alternateSubrangeSelect d-flex mt-3 align-items-center">
          <div class="label">Other suits go to:</div>
          <b-form-select
            :value="this.alternateSubrangeIndex"
            :options="alternateSubrangeSelectOptions"
            @input="value => $emit('changeAlternateSubrangeIndex', value)"
          />
        </div>

        <div
          class="fullComboSelectToggle d-flex justify-content-end mt-3"
          :class="{ disabled: !doesQuickSelectMatchFullSelect }"
          @click="toggleFullComboSelect"
        >
          <div v-if="isFullComboSelectToggled">less <fa icon="caret-up" /></div>
          <div v-else>more <fa icon="caret-down" /></div>
        </div>

        <div class="fullComboSelect mt-3" v-if="isFullComboSelectToggled">
          <div v-for="(row, i) in fullSelect" :key="i" class="d-flex justify-content-center">
            <div class="d-flex" v-for="comboData in row" :key="comboData.comboString">
              <div
                class="combo"
                :class="{ active: comboData.selected }"
                @click="() => toggleCombo(comboData.comboString, !comboData.selected)"
              >
                <Suit v-for="(suit, i) in comboData.comboString.split('')" :key="i" :suit="suit" />
              </div>
            </div>
          </div>
        </div>
      </b-card-body>

      <b-card-body v-else>
        only available postflop
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import Vue from "vue";
import countBy from "lodash/countBy";
import chunk from "lodash/chunk";
import {
  cardsToSuits,
  allSuitComboStrings,
  suitComboStringsToSuitQuickSelection,
  suitQuickSelectionToSuitComboStrings,
} from "../helpers/cards";
import Suit from "./Suit";

export default {
  name: "SuitSelect",
  components: { Suit },
  props: {
    // v-model value: selected suit combos
    value: { type: Array, required: true },
    subranges: { type: Array, required: true },
    activeSubrangeIndex: { type: Number, required: true },
    alternateSubrangeIndex: { type: Number, required: true },
    board: { type: Array, required: true },
  },
  data() {
    return {
      isFullComboSelectToggled: false,
      checkboxes: suitComboStringsToSuitQuickSelection(this.value),
    };
  },
  computed: {
    isAvailable() {
      return this.board.length >= 3;
    },
    quickSelectableSuits() {
      // The only suits relevant for quick selection are the ones that can make flushes,
      // so they have to be present on the flop
      const suits = cardsToSuits(this.board);
      const flopSuits = cardsToSuits(this.board.slice(0, 3));
      const suitCounts = countBy(suits);
      // Unique flop suits sorted (desc) by total number of cards of the suit on the board
      const sortedFlopSuits = [...new Set(flopSuits)].sort((a, b) => {
        return suitCounts[b] - suitCounts[a];
      });
      return sortedFlopSuits;
    },
    fullSelect() {
      const suitComboData = allSuitComboStrings.map(comboString => {
        return {
          comboString,
          selected: this.value.includes(comboString),
        };
      });
      return chunk(suitComboData, 4);
    },
    doesQuickSelectMatchFullSelect() {
      return this.value.length === suitQuickSelectionToSuitComboStrings(this.checkboxes).length;
    },
    alternateSubrangeSelectOptions() {
      return this.subranges
        .map((data, i) => ({ value: i, text: data.label }))
        .filter(options => options.value !== this.activeSubrangeIndex);
    },
  },
  methods: {
    reset() {
      this.$emit("input", []);
    },
    toggleCheckbox(value, suit, type) {
      // Only one option per suit can be selected
      if (value === true) {
        type === "any" ? (this.checkboxes[suit].high = false) : (this.checkboxes[suit].any = false);
      }
      Vue.nextTick(() => {
        this.updateSelectedCombosFromCheckboxes();
      });
    },
    toggleCombo(comboString, setToActive) {
      if (setToActive) {
        this.$emit("input", [...this.value, comboString]);
      } else {
        const comboStrings = this.value.filter(_comboString => _comboString !== comboString);
        this.$emit("input", comboStrings);
      }
    },
    toggleFullComboSelect() {
      // If full select contains combos that can't be mapped onto quick select,
      // prevent full select from getting toggled off
      if (!this.doesQuickSelectMatchFullSelect) return;
      this.isFullComboSelectToggled = !this.isFullComboSelectToggled;
    },
    updateSelectedCombosFromCheckboxes() {
      this.$emit("input", suitQuickSelectionToSuitComboStrings(this.checkboxes));
    },
  },
  watch: {
    value(combos) {
      this.checkboxes = suitComboStringsToSuitQuickSelection(combos);
    },
    // When the board changes, remove any previous suit selections that don't match the current board
    quickSelectableSuits(suits) {
      for (const suit of Object.keys(this.checkboxes)) {
        if (!suits.includes(suit)) {
          this.checkboxes[suit] = { any: false, high: false };
        }
      }
      this.updateSelectedCombosFromCheckboxes();
    },
  },
};
</script>

<style lang="scss" scoped>
.suitSelectComponent {
  width: 300px;
}
.fullComboSelect {
  margin: 0 auto;
}
.fullComboSelectToggle {
  cursor: pointer;
  &.disabled {
    cursor: default;
    opacity: 0.2;
  }
}
.alternateSubrangeSelect {
  .label {
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    margin-right: 10px;
  }
}
.combo {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 5px;
  border: 1px solid #888;
  width: 50px;
  height: 30px;
  cursor: pointer;
  &.active {
    background: bisque;
  }
}
</style>
